import type { GridApi } from "ag-grid-community"

export type PaginationApi = Pick<
  GridApi,
  | "paginationGetPageSize"
  | "paginationGetCurrentPage"
  | "paginationGetTotalPages"
  | "paginationGoToPage"
  | "setGridOption"
>

/** Rows that fit the grid viewport, or undefined while the viewport has no height yet. */
export function measureAutoPageSize(container: HTMLElement | null, api: GridApi) {
  const viewport = container?.querySelector<HTMLElement>(".ag-grid-viewport")
  if (!viewport || viewport.clientHeight <= 0) return undefined
  const rowHeight = api.getSizesForCurrentTheme()?.rowHeight
  if (!rowHeight || rowHeight <= 0) return undefined
  return Math.max(Math.floor(viewport.clientHeight / rowHeight), 1)
}

function goToSavedPage(api: PaginationApi, page: number) {
  const target = Math.max(Math.min(page, api.paginationGetTotalPages() - 1), 0)
  if (api.paginationGetCurrentPage() !== target) api.paginationGoToPage(target)
}

/**
 * Auto page size and saved-page restore for a paginated grid. Setting paginationPageSize jumps
 * AG Grid to page 0, so each size change restores a position afterwards. When the viewport can't
 * be measured at first render, the saved page is held until it can: restoring it at the
 * interim page size would land on a different page once the measured size is applied.
 */
export function createAutoPagination<A extends PaginationApi>(
  measure: (api: A) => number | undefined,
) {
  let pendingPage: number | null = null

  // Returns false while the viewport can't be measured.
  const applyPageSize = (api: A, keepFirstRow: boolean) => {
    const newPageSize = measure(api)
    if (newPageSize === undefined) return false
    const oldPageSize = api.paginationGetPageSize()
    if (oldPageSize === newPageSize) return true
    const firstRow = api.paginationGetCurrentPage() * oldPageSize
    api.setGridOption("paginationPageSize", newPageSize)
    if (keepFirstRow && firstRow > 0) api.paginationGoToPage(Math.floor(firstRow / newPageSize))
    return true
  }

  return {
    restoreSavedPage(api: A, savedPage: number, auto: boolean) {
      pendingPage = null
      if (auto && !applyPageSize(api, false)) {
        if (savedPage > 0) pendingPage = savedPage
        return
      }
      if (savedPage > 0) goToSavedPage(api, savedPage)
    },
    /** Re-fits the page size after a layout or model change, keeping the rows on screen. */
    remeasure(api: A) {
      const page = pendingPage
      if (!applyPageSize(api, page === null)) return
      if (page === null) return
      pendingPage = null
      goToSavedPage(api, page)
    },
    /** Applies the measured size from page 0, as an explicit rows-per-page change does. */
    resetPageSize(api: A) {
      pendingPage = null
      applyPageSize(api, false)
    },
    cancelPendingRestore() {
      pendingPage = null
    },
  }
}
